import axios from 'axios';

const API_BASE_URL = 'https://zedlenders.pickmesms.com';

class DiscussionService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.isConnected = false;
  }

  // WebSocket connection for real-time features
  connectWebSocket(userId, companyId, token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    // Use localhost for development, production URL for production
    const wsUrl = process.env.NODE_ENV === 'development' 
      ? `ws://localhost:8080/ws/discussion/${companyId}?token=${token}`
      : `wss://zedlenders.pickmesms.com/ws/discussion/${companyId}?token=${token}`;
    
    try {
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('Discussion WebSocket connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.sendMessage({
          type: 'join',
          user_id: userId,
          company_id: companyId
        });
      };

      this.ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      };

      this.ws.onclose = () => {
        console.log('Discussion WebSocket disconnected');
        this.isConnected = false;
        this.attemptReconnect(userId, companyId, token);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }

  attemptReconnect(userId, companyId, token) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connectWebSocket(userId, companyId, token);
      }, Math.pow(2, this.reconnectAttempts) * 1000);
    }
  }

  sendMessage(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  handleMessage(data) {
    try {
      const { type } = data;
      
      if (this.listeners.has(type)) {
        this.listeners.get(type).forEach(callback => {
          try {
            callback(data);
          } catch (error) {
            console.error(`Error in message callback for type ${type}:`, error);
          }
        });
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error, data);
    }
  }

  addEventListener(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type).push(callback);
  }

  removeEventListener(type, callback) {
    if (this.listeners.has(type)) {
      const callbacks = this.listeners.get(type);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.listeners.clear();
    this.isConnected = false;
  }

  // API methods
  async getTopics(companyId, token) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/items/discussion_topics?filter[company]=${companyId}&sort=-date_created&fields=*,user_created.first_name,user_created.last_name,user_created.avatar`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching topics:', error);
      throw error;
    }
  }

  async createTopic(topicData, token) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/items/discussion_topics`,
        topicData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating topic:', error);
      throw error;
    }
  }

  async getMessages(topicId, token) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/items/discussion_messages?filter[topic_id]=${topicId}&sort=date_created&fields=*,user.first_name,user.last_name,user.avatar`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }

  async sendChatMessage(messageData, token) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/items/discussion_messages`,
        messageData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Send real-time notification
      this.sendMessage({
        type: 'new_message',
        message: response.data.data,
        topic_id: messageData.topic_id
      });

      return response.data.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async updateUserPresence(userId, companyId, status, currentTopic, token) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/items/user_presence`,
        {
          user_id: userId,
          company_id: companyId,
          status: status,
          current_topic: currentTopic,
          last_seen: new Date().toISOString()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Broadcast presence update
      this.sendMessage({
        type: 'presence_update',
        user_id: userId,
        status: status,
        current_topic: currentTopic
      });

      return response.data.data;
    } catch (error) {
      console.error('Error updating presence:', error);
      throw error;
    }
  }

  async getOnlineUsers(companyId, token) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/items/user_presence?filter[company_id]=${companyId}&filter[status][_neq]=offline`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching online users:', error);
      throw error;
    }
  }

  async addReaction(messageId, userId, reaction, token) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/items/message_reactions`,
        {
          message_id: messageId,
          user_id: userId,
          reaction: reaction
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Send real-time reaction update
      this.sendMessage({
        type: 'reaction_added',
        message_id: messageId,
        user_id: userId,
        reaction: reaction
      });

      return response.data.data;
    } catch (error) {
      console.error('Error adding reaction:', error);
      throw error;
    }
  }

  async markTopicAsRead(topicId, userId, token) {
    try {
      await axios.post(
        `${API_BASE_URL}/items/topic_read_status`,
        {
          topic_id: topicId,
          user_id: userId,
          last_read: new Date().toISOString()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
    } catch (error) {
      console.error('Error marking topic as read:', error);
    }
  }

  // Notification methods
  async requestNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }

  showNotification(title, body, icon = null, onClick = null) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: icon || '/logo192.png',
        badge: '/logo192.png',
        tag: 'discussion-forum',
        requireInteraction: false,
        silent: false
      });

      if (onClick) {
        notification.onclick = onClick;
      }

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }

  // Typing indicators
  sendTypingIndicator(topicId, userId, isTyping) {
    this.sendMessage({
      type: 'typing',
      topic_id: topicId,
      user_id: userId,
      is_typing: isTyping
    });
  }
}

export default new DiscussionService();